import { useReducer, useEffect, useCallback, useState } from 'react'
import api, { IHRLocation, IDepartment, IWorkplaceGroup, ISpace, IEmployee, IUsageType } from '@api'
import { uniqueId, last } from 'lodash'
import orderBy from 'lodash/fp/orderBy'
import { ISelection } from '@hooks/useSelection'
import { useShouldReload } from '@hooks/reloadContext'
import { IGridTableCell, GridTableStatusEnum } from '@ui'
import { DateUtil, TIME } from '@utils/dateUtil'
import { Order, IBooking, ICalendarEvents } from '@api/types'
import get from 'lodash/get'
import {
  BaseTableFilters,
  Sort,
  UseBookingsTableHook,
  Actions,
  ActionBase,
} from '../../CurrentBookingsTable/types'

export interface Filters extends BaseTableFilters {
  locations: any[]
  HRLocations: IHRLocation[]
  departments: IDepartment[]
  WPGs: IWorkplaceGroup[]
  spaces: ISpace[]
  employees: IEmployee[]
  usageTypes: IUsageType[]
}

interface State {
  reqId: string
  status: GridTableStatusEnum
  count: number
  // toDate: Date | null
  // fromDate: Date | null
  items: IGridTableCell<IBooking>[]
  data: { current: { id: string; data: IBooking }[]; past: { id: string; data: IBooking }[] }
  sort: Sort
  filters: BaseTableFilters
}

export interface UseBookingsHook extends UseBookingsTableHook, State {
  sortBy: (column: string) => void
  selection?: ISelection
  fetchBookings: () => void
  updateItems: (param: boolean) => void
  startDataSync: (param: boolean) => void
  stopDataSync: () => void
  resetFilters: () => void
  applyFilters: (filters: BaseTableFilters) => void
  updateFilters: (filters: Partial<BaseTableFilters>) => void
}

type SetItems = {
  type: Actions.SET
  items: IGridTableCell<IBooking>[]
}

type LoadItems = {
  type: Actions.LOAD
  reqId: string
  items: IGridTableCell<IBooking>[]
  data: {
    current: { id: string; data: IBooking }[]
    past: { id: string; data: IBooking }[]
  }
  count: number
  // fromDate: Date
  // toDate: Date
}

type Action = ActionBase | LoadItems | SetItems

type TOrder = 'asc' | 'desc'

const persistedState: State = {
  reqId: uniqueId('request'),
  status: GridTableStatusEnum.LOADING,
  count: 0,
  items: [],
  data: { current: [], past: [] },
  sort: { column: 'startDate', order: Order.ASC },
  filters: {
    query: '',
    fromDate: null,
    toDate: null,
  },
}

export default function useBookingModal(): UseBookingsHook {
  const [state, dispatch] = useReducer(reducer, persistedState)
  const [time, setTime] = useState<NodeJS.Timeout>(0 as unknown as NodeJS.Timeout)
  const selection = undefined
  const shouldReload = useShouldReload()

  const orderItems = useCallback(
    orderBy<IGridTableCell<IBooking>>((item) => get(item, `data.${state.sort.column}`)),
    [state.sort.column]
  )

  const updateItems = (isCurrent: boolean) => {
    dispatch({
      type: Actions.SET,
      items: isCurrent ? state.data.current : state.data.past,
    })
  }

  const fetchBookings = (isCurrent: boolean) => {
    const reqId = uniqueId('request')

    dispatch({ type: Actions.LOAD_START_WITHOUT_SKELETON, reqId })

    let today
    api.booking.current(DateUtil().sub(6, TIME.MONTHS).toISOString()).then((bookings: any) => {
      const data = bookings.reduce(
        (a: any, { id, ...b }: { id: string; b: any }) => {
          const timezone =
            get(
              b,
              get(b, 'calendar')
                ? 'calendar.space.floor.building?.timezone'
                : 'space.floor.building?.timezone'
            ) || DateUtil.defaultTz()
          today = DateUtil.tz(timezone)
          if (
            DateUtil(get(b, get(b, 'calendar') ? 'end' : 'endDate'))
              .tz(timezone)
              .isBefore(today)
          ) {
            a.past.push({ id, data: { id, ...b } })
          } else a.current.push({ id, data: { id, ...b } })
          return a
        },
        {
          current: [] as { id: string; data: IBooking }[],
          past: [] as { id: string; data: IBooking }[],
        }
      )
      const items = isCurrent ? data.current : data.past

      const orderedItemsByStartDate = orderBy((item) => get(item, 'data.startDate'), 'desc', items)

      dispatch({
        type: Actions.LOAD,
        count: bookings.length,
        data,
        items: orderItems(state.sort.order.toLocaleLowerCase() as TOrder, items),
        reqId,
      })
    })
  }
  const startDataSync = (isCurrent: boolean) => {
    stopDataSync()
    fetchBookings(isCurrent)
    setTime(
      setInterval(() => {
        fetchBookings(isCurrent)
      }, 60000)
    )
  }

  const stopDataSync = () => clearInterval(time)

  //api request without skeleton and with load all items in state.first
  useEffect(() => fetchBookings(true), [shouldReload.bookings])

  useEffect(() => {
    dispatch({
      type: Actions.SET,
      items: orderItems(state.sort.order.toLocaleLowerCase() as TOrder, state.items),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sort])

  return {
    ...state,
    selection,
    sortBy: (column: string) => {
      dispatch({ type: Actions.SORT, column })
    },
    fetchBookings: () => fetchBookings(true),
    startDataSync,
    stopDataSync,
    updateItems,
    resetFilters: () => {},
    applyFilters: () => {},
    updateFilters: () => {},
  }
}

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case Actions.SET:
      return {
        ...state,
        items: action.items,
      }
    case Actions.LOAD_START:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.LOADING,
      }
    case Actions.LOAD_START_WITHOUT_SKELETON:
      return {
        ...state,
        reqId: action.reqId,
        status: GridTableStatusEnum.IDLE,
      }

    case Actions.ERROR:
      return action.reqId === state.reqId ? { ...state, status: GridTableStatusEnum.IDLE } : state

    case Actions.LOAD:
      return action.reqId === state.reqId
        ? {
            ...state,
            status: GridTableStatusEnum.IDLE,
            count: action.count,
            data: action.data,
            items: action.items,
            // fromDate: action.fromDate,
            // toDate: action.toDate,
          }
        : state

    case Actions.SORT:
      return {
        ...state,
        status: GridTableStatusEnum.IDLE,
        sort: {
          column: action.column,
          order:
            state.sort.column === action.column
              ? state.sort.order === Order.ASC
                ? Order.DESC
                : Order.ASC
              : Order.ASC,
        },
      }
    default:
      return state
  }
}
